import { useEffect, useId, useRef, useState } from "react"
import DeckCardView from "@/components/DeckBrowser/DeckCardView"
import DeckDetail from "@/components/DeckBrowser/DeckDetail"
import DeckGrid from "@/components/DeckBrowser/DeckGrid"
import ViewIcon from "@/components/DeckBrowser/ViewIcon"
import { NEUTRAL_TINT, setPageTint, tintFor, wipePageTint } from "@/components/DeckBrowser/pageWipe"
import GridBackdrop from "@/components/GridBackdrop/GridBackdrop"
import LokaMark from "@/components/LokaMark/LokaMark"
import { cardIdsForFamily, parseCardId } from "@/content/cards"
import { DECK_BROWSER_COPY } from "@/content/cardsRound"
import { familiesInPlay } from "@/content/families"
import type { CardId } from "@/types"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/DeckBrowser.module.css"

/*
 * The deck, readable at any point, picking nothing.
 *
 * Reachable from the lobby, from a guest's waiting screen, from the facilitator's
 * rail and from `/deck` on its own. All of those are moments where someone wants
 * to know what is in the deck before or while it matters. It says out loud that
 * nothing is being picked here, because the cards are identical to the ones on
 * the picking screen and a room that thought otherwise would build a hand that
 * does not exist.
 *
 */

// The active deck, in id order (family 5, Monitors, is retired). When the browser
// is opened inside a session it uses that session's families instead, so an old
// session that still plays Monitors shows it.
const ALL_FAMILIES = [1, 2, 3, 4, 6, 7]

interface DeckBrowserProps {
  /** Defaults to the whole deck: this is a read, not the session's hand. */
  families?: number[]
  /**
   * Given when the browser is a layer over something — the lobby, a waiting
   * screen. Omitted on `/deck`, where the nav is the way out and a close button
   * would have nowhere to go.
   */
  onClose?: () => void
}

export default function DeckBrowser({ families, onClose }: DeckBrowserProps) {
  const titleId = useId()
  const inPlayNumbers = families ?? ALL_FAMILIES
  // Deal order, not the printed numbering: every screen that shows the deck as
  // a deck goes through `familiesInPlay`.
  const inPlay = familiesInPlay(inPlayNumbers)

  const [view, setView] = useState<"single" | "grid">("single")
  // Grid filter: a family number, or -1 for "All families" (the default).
  const [filter, setFilter] = useState(-1)
  const [family, setFamily] = useState<FamilyNumber>(inPlay[0]?.n ?? 1)
  const [index, setIndex] = useState(0)
  const [detailOpen, setDetailOpen] = useState(false)
  // The card the detail was opened from — the overlay morphs out of and back into
  // it. A ref, not state: it drives an imperative animation, not a render.
  const sourceRef = useRef<HTMLElement | null>(null)

  // A full-screen layer with no other way out on a keyboard. The detail overlay
  // owns its own Escape (it closes itself), so here Escape only closes the
  // browser, and only when the overlay is not up.
  useEffect(() => {
    if (!onClose) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      if (detailOpen) return
      onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose, detailOpen])

  useEffect(() => {
    if (!onClose) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [onClose])

  // DeckBrowser owns the page tint — card view washes the page in the family
  // colour, grid is neutral — and both wipes (family chip, view toggle). The tint
  // is set imperatively by the wipe handlers, never from a reactive effect (which
  // would beat the wipe to the new colour); this effect is just the initial paint
  // and the clear on leave, so no other facilitator screen stays tinted.
  useEffect(() => {
    setPageTint(view === "single" ? tintFor(family) : NEUTRAL_TINT)
    return () => setPageTint(NEUTRAL_TINT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (inPlay.length === 0) return null

  const cards = cardIdsForFamily(family)
  const at = Math.min(index, Math.max(cards.length - 1, 0))
  const cardId: CardId | undefined = cards[at]

  // Card-view family switch — the chip hands up its element so the wipe grows
  // from it; the tint lands when the disc does.
  const pickFamily = (n: FamilyNumber, el: HTMLElement) => {
    if (n === family) return
    wipePageTint(el, tintFor(n))
    setFamily(n)
    setIndex(0)
  }

  // Card ↔ grid: wipe to the family tint (card) or the neutral wash (grid), from
  // the toggle. Content swaps at once; the old background is held until the disc
  // lands, as in the handoff.
  const switchView = (next: "single" | "grid", el: HTMLElement) => {
    if (next === view) return
    wipePageTint(el, next === "single" ? tintFor(family) : NEUTRAL_TINT)
    setView(next)
    if (next === "single") setIndex(0)
  }

  const goTo = (id: CardId) => {
    const parsed = parseCardId(id)
    if (!parsed) return
    setFamily(parsed.family as FamilyNumber)
    // The stack index is the card's POSITION in its family, not its id-number.
    // The six-family re-cut left some ids out of order — family 1's 1.3 is `f1c5`
    // at position 2 — so `parsed.index - 1` opened the neighbour (1.3 ⇄ 1.4).
    const position = cardIdsForFamily(parsed.family).indexOf(id)
    setIndex(position < 0 ? 0 : position)
  }

  // Both views open the same detail overlay for the current card; the clicked
  // element is kept so the overlay can morph out of and back into it.
  const openDetail = (el: HTMLElement) => {
    sourceRef.current = el
    setDetailOpen(true)
  }

  const openDetailFromGrid = (id: CardId, el: HTMLElement) => {
    goTo(id)
    sourceRef.current = el
    setDetailOpen(true)
  }

  // One toggle, shown on the right of the tabs row in card view and above the
  // grid in grid view, so it stays put when the view changes.
  const toggle = (
    <div className={styles.viewToggle} role="group" aria-label="Browse mode">
      <button
        type="button"
        aria-pressed={view === "single"}
        className={view === "single" ? `${styles.viewBtn} ${styles.viewBtnOn}` : styles.viewBtn}
        title={DECK_BROWSER_COPY.singleView}
        aria-label={DECK_BROWSER_COPY.singleView}
        onClick={(event) => switchView("single", event.currentTarget)}
      >
        <ViewIcon mode="single" />
      </button>
      <button
        type="button"
        aria-pressed={view === "grid"}
        className={view === "grid" ? `${styles.viewBtn} ${styles.viewBtnOn}` : styles.viewBtn}
        title={DECK_BROWSER_COPY.gridView}
        aria-label={DECK_BROWSER_COPY.gridView}
        onClick={(event) => switchView("grid", event.currentTarget)}
      >
        <ViewIcon mode="grid" />
      </button>
    </div>
  )

  return (
    <div
      /* A layer over the lobby is fixed and full-bleed; on its own route it is
         just the page, or it would cover the nav that is the only way out. */
      className={
        onClose ? `${styles.screen} ${styles.withMark}` : `${styles.screen} ${styles.inPage}`
      }
      role={onClose ? "dialog" : undefined}
      aria-modal={onClose ? "true" : undefined}
      // Named by its own heading rather than a duplicate label, so the two
      // cannot drift.
      aria-labelledby={titleId}
    >
      {onClose ? (
        <>
          <GridBackdrop />
          <LokaMark className={styles.mark} />
        </>
      ) : null}

      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title} id={titleId}>
            {onClose ? DECK_BROWSER_COPY.titleInSession : DECK_BROWSER_COPY.titleStandalone}
          </h1>
          <p className={styles.blurb}>
            {onClose ? DECK_BROWSER_COPY.blurbInSession : DECK_BROWSER_COPY.blurbStandalone}
          </p>
        </div>

        {onClose ? (
          <button type="button" className={styles.back} onClick={onClose}>
            <span aria-hidden="true">←</span>
            {DECK_BROWSER_COPY.backToLobby}
          </button>
        ) : null}
      </header>

      {view === "single" ? (
        <DeckCardView
          inPlay={inPlay}
          family={family}
          index={index}
          onFamilyPick={pickFamily}
          onIndex={setIndex}
          onOpen={openDetail}
          active={!detailOpen}
          toggle={toggle}
        />
      ) : (
        <DeckGrid
          inPlay={inPlay}
          filter={filter}
          onFilter={setFilter}
          onOpen={openDetailFromGrid}
          toggle={toggle}
        />
      )}

      {detailOpen && cardId ? (
        <DeckDetail cardId={cardId} sourceRef={sourceRef} onClose={() => setDetailOpen(false)} />
      ) : null}
    </div>
  )
}
